import React, { useMemo, useState, useEffect, useCallback } from "react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend
} from "recharts";
import { 
  Users, Package, ShieldCheck, UserX, Activity
} from "lucide-react";
import { clientsList, ordersList, scoring } from "../../../../../api/auth";
import ServiceUnavailable from "../../../../component/ServiceUnavailable";

/* --- SETTINGS & COLORS --- */
const COLORS = {
  GREEN: "#10b981", // Emerald 500
  BLUE: "#3b82f6",  // Blue 500
  RED: "#f43f5e",   // Rose 500
  BLACKLIST: "#0f172a", // Slate 900
  PENDING: "#94a3b8"  // Slate 400
};

export default function ClientsDashboard() {
  const [data, setData] = useState({ clients: [], orders: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cRes, oRes] = await Promise.all([clientsList(), ordersList()]);
      const clients = cRes.data?.results || cRes.data || [];
      const orders = oRes.data?.results || oRes.data || [];

      // Logic dyal Scoring bach n-tal3o l-charts s'hah
      const enrichedClients = await Promise.all(clients.slice(0, 20).map(async (c) => {
        try {
            const sRes = await scoring(c.phone);
            const score = Math.round(sRes.data?.final?.score_100 || 0);
            return { ...c, score };
        } catch { return { ...c, score: 0 }; }
      }));

      setData({ clients: enrichedClients, orders, stats: {} });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAllData(); }, [loadAllData]);

  /* --- DATA PROCESSING FOR CHARTS --- */
  const chartData = useMemo(() => {
    // 1. Trust Distribution (Pie Chart)
    const dist = { GREEN: 0, BLUE: 0, RED: 0, BLACKLIST: 0 };
    data.clients.forEach(c => {
      if (c.score >= 75) dist.GREEN++;
      else if (c.score >= 50) dist.BLUE++;
      else if (c.score >= 25) dist.RED++;
      else dist.BLACKLIST++;
    });

    const pieData = Object.keys(dist).map(key => ({ name: key, value: dist[key] }));

    // 2. Revenue Simulation (Bar Chart) - can be grouped by city or status
    const barData = [
      { name: "Confirmés", total: data.orders.filter(o => o.status === 'confirmed').length, color: "#3b82f6" },
      { name: "Livrés", total: data.orders.filter(o => o.status === 'delivered').length, color: "#10b981" },
      { name: "Annulés", total: data.orders.filter(o => o.status === 'cancelled').length, color: "#f43f5e" },
    ];

    return { pieData, barData };
  }, [data]);

  if (loading) return <div className="p-20 text-center font-black text-slate-300 dark:text-slate-600 animate-pulse">GENERATING INSIGHTS...</div>;

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] dark:bg-slate-950 p-4 md:p-10">
        <div className="max-w-3xl mx-auto">
          <ServiceUnavailable label="Le service clients" onRetry={loadAllData} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] dark:bg-slate-950 p-4 md:p-10 space-y-10">

      {/* 1. TOP HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-slate-100 tracking-tighter uppercase">Operations Dashboard</h1>
          <p className="text-slate-500 dark:text-slate-400 font-bold flex items-center gap-2">
            <Activity size={16} className="text-indigo-500 dark:text-indigo-400" />
            Live performance tracking & trust metrics
          </p>
        </div>
        <div className="bg-white dark:bg-slate-900 border-2 border-slate-900 dark:border-slate-700 px-6 py-3 rounded-2xl shadow-[8px_8px_0px_0px_rgba(15,23,42,1)] dark:shadow-none">
          <span className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Total Revenue</span>
          <div className="text-2xl font-black text-slate-900 dark:text-slate-100">
             {new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(data.orders.reduce((s,o) => s + Number(o.total_amount || 0), 0))}
          </div>
        </div>
      </div>

      {/* 2. STATS GRID (Bento Style) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <MiniCard title="Total Clients" value={data.clients.length} icon={<Users />} color="bg-indigo-500" />
        <MiniCard title="Total Orders" value={data.orders.length} icon={<Package />} color="bg-blue-500" />
        <MiniCard title="Green Trust" value={data.clients.filter(c => c.score >= 75).length} icon={<ShieldCheck />} color="bg-emerald-500" />
        <MiniCard title="Blacklisted" value={data.clients.filter(c => c.score < 25).length} icon={<UserX />} color="bg-slate-900" />
      </div>

      {/* 3. CHARTS SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Chart 1: Trust Distribution */}
        <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-xl shadow-slate-200/40 dark:shadow-none">
          <h3 className="text-lg font-black uppercase tracking-tight mb-8 dark:text-slate-100">Trust Categories Distribution</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData.pieData}
                  innerRadius={80}
                  outerRadius={100}
                  paddingAngle={8}
                  dataKey="value"
                >
                  {chartData.pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.name]} stroke="none" />
                  ))}
                </Pie>
                <Tooltip />
                <Legend iconType="circle" wrapperStyle={{ paddingTop: "20px", fontWeight: "900", fontSize: "12px" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 2: Order Status Performance */}
        <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-xl shadow-slate-200/40 dark:shadow-none">
          <h3 className="text-lg font-black uppercase tracking-tight mb-8 dark:text-slate-100">Order Status Analysis</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData.barData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 900 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                <Bar dataKey="total" radius={[10, 10, 10, 10]} barSize={40}>
                   {chartData.barData.map((entry, index) => (
                     <Cell key={`cell-${index}`} fill={entry.color} />
                   ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      {/* 4. RECENT ACTIVITY TABLE */}
      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-xl overflow-hidden">
        <div className="p-8 border-b border-slate-50 dark:border-slate-800 flex justify-between items-center">
          <h3 className="text-lg font-black uppercase tracking-tight dark:text-slate-100">Top Scoring Clients</h3>
          <button className="text-xs font-black text-indigo-600 dark:text-indigo-400 hover:underline">Voir tout</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50/50 dark:bg-slate-800/60">
              <tr>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase">Client</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase">Phone</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase">Trust Index</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
              {data.clients.slice(0, 5).map(c => (
                <tr key={c.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-all">
                  <td className="px-8 py-4 font-black text-slate-800 dark:text-slate-100 uppercase text-sm">{c.full_name}</td>
                  <td className="px-8 py-4 text-xs font-bold text-slate-500 dark:text-slate-400">{c.phone}</td>
                  <td className="px-8 py-4">
                     <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden max-w-[100px]">
                        <div className="h-full bg-indigo-500" style={{ width: `${c.score}%` }} />
                     </div>
                  </td>
                  <td className="px-8 py-4 text-right">
                    <span className="text-[10px] font-black px-3 py-1 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-lg">VERIFIED</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* --- REUSABLE MINI CARD --- */
function MiniCard({ title, value, icon, color }) {
  return (
    <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm flex items-center gap-5 group hover:shadow-xl transition-all hover:-translate-y-1">
      <div className={`w-14 h-14 rounded-2xl ${color} flex items-center justify-center text-white shadow-lg`}>
        {React.cloneElement(icon, { size: 24 })}
      </div>
      <div>
        <div className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em]">{title}</div>
        <div className="text-2xl font-black text-slate-900 dark:text-slate-100 tracking-tighter">{value}</div>
      </div>
    </div>
  );
}
